import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import ProductCard from "@/components/ProductCard";
import { PRODUCTS, PRODUCT_DISCIPLINES, PRODUCT_MOODS } from "@/data/products";
import { ELEMENTS } from "@/data/elements";
import { useStudio } from "@/context/StudioContext";
import { useSEO, breadcrumb, SITE } from "@/lib/useSEO";

// Listing items are plain ListItems (name + url only). They must NOT be typed
// as `Product`: without a published price Google rejects product snippets with
// "Either 'offers', 'review' or 'aggregateRating' should be specified".
const COLLECTION_ITEMLIST = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "The My Floor Studio Collection",
  description:
    "Curated luxury flooring finishes across Timber, Carpet, Tile and Stone — hand-picked by My Floor Studio's Drummoyne showroom for Sydney architects and designers.",
  numberOfItems: PRODUCTS.length,
  itemListElement: PRODUCTS.map((p, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: `${p.name} · ${p.discipline}`,
    url: `${SITE}/collection/${p.id}`,
  })),
};

export default function Collection() {
  const [filter, setFilter] = useState("All");
  const [mood, setMood] = useState("All");
  const { openSample } = useStudio();
  useSEO({
    title:
      "The Collection · Luxury Timber, Carpet, Tile & Stone Flooring — My Floor Studio",
    description:
      "24 top-of-line luxury flooring finishes across Timber, Carpet, Tile and Stone. Filter by element and colour mood — Warm, Cool, Neutral, Dark, Light, Natural. Request a sample kit for your Sydney project.",
    path: "/collection",
    keywords:
      "luxury flooring collection, engineered timber, wide-plank oak, wool carpet, marble tile, terrazzo, natural stone, porcelain tile, herringbone parquet, chevron flooring, Sydney architects, interior designer samples",
    imageAlt: "The My Floor Studio Collection — curated luxury flooring finishes",
    jsonLd: [
      COLLECTION_ITEMLIST,
      breadcrumb([
        { name: "Home", path: "/" },
        { name: "Collection", path: "/collection" },
      ]),
    ],
  });

  const list = useMemo(() => {
    return PRODUCTS.filter((p) => {
      if (filter !== "All" && p.discipline !== filter) return false;
      if (mood !== "All" && !(p.moods || []).includes(mood)) return false;
      return true;
    });
  }, [filter, mood]);

  return (
    <div className="min-h-screen bg-[#121315] grain">
      <Nav />
      <main className="pt-32 md:pt-40">
        <section className="mx-auto max-w-[1600px] px-6 md:px-12 pb-16">
          <p className="text-[#C9A876] text-[11px] tracking-[0.4em] uppercase mb-8">The Collection</p>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
            <h1 className="font-serif-display italic text-[#F5F4F0] font-light tracking-tight text-5xl sm:text-6xl md:text-7xl xl:text-8xl leading-[0.95] max-w-3xl">
              Signature elements
            </h1>
            <p className="text-[#D6D1CA]/70 font-body max-w-sm text-sm md:text-base">
              A curated library of top-of-line finishes across every element. Studied by our team,
              chosen for the way they hold their beauty for decades.
            </p>
          </div>

          {/* Element filter row */}
          <div className="mt-14 flex flex-wrap gap-3" data-testid="collection-filter">
            {PRODUCT_DISCIPLINES.map((d) => {
              const on = filter === d;
              return (
                <button
                  key={d}
                  data-testid={`collection-filter-${d.toLowerCase()}`}
                  onClick={() => setFilter(d)}
                  className="rounded-full border px-6 py-2 text-[11px] tracking-[0.2em] uppercase"
                  style={{
                    borderColor: on ? "#8C7355" : "rgba(214,209,202,0.22)",
                    background: on ? "rgba(140,115,85,0.2)" : "transparent",
                    color: on ? "#F5F4F0" : "rgba(214,209,202,0.75)",
                    transition: "all 0.35s ease",
                  }}
                >
                  {d}
                </button>
              );
            })}
          </div>

          {/* Mood filter row — colour family, smaller and quieter than element */}
          <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="collection-mood-filter">
            <span className="text-[#C9A876]/70 text-[9px] tracking-[0.3em] uppercase mr-1">
              Colour mood
            </span>
            {PRODUCT_MOODS.map((m) => {
              const on = mood === m;
              return (
                <button
                  key={m}
                  data-testid={`collection-mood-${m.toLowerCase()}`}
                  onClick={() => setMood(m)}
                  className="rounded-full border px-4 py-1.5 text-[10px] tracking-[0.22em] uppercase"
                  style={{
                    borderColor: on ? "#C9A876" : "rgba(214,209,202,0.15)",
                    background: on ? "rgba(201,168,118,0.14)" : "transparent",
                    color: on ? "#F5F4F0" : "rgba(214,209,202,0.55)",
                    transition: "all 0.35s ease",
                  }}
                >
                  {m}
                </button>
              );
            })}
          </div>

          {/* Element landing page links — deep dives per element */}
          <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2" data-testid="collection-element-links">
            <span className="text-[#D6D1CA]/40 text-[9px] tracking-[0.3em] uppercase">Deep dive</span>
            {ELEMENTS.map((e) => (
              <Link
                key={e.slug}
                to={`/${e.slug}`}
                data-testid={`collection-element-link-${e.slug}`}
                className="text-[#C9A876] text-[10px] tracking-[0.28em] uppercase link-underline hover:text-[#dcb884]"
                style={{ transition: "color 0.4s ease" }}
              >
                {e.name} →
              </Link>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-[1600px] px-6 md:px-12 pb-32">
          {list.length === 0 && (
            <p className="text-[#D6D1CA]/50 text-sm font-body py-16 text-center">
              No finishes match this combination. Try broadening the mood filter.
            </p>
          )}
          <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {list.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </motion.div>

          <p className="text-[#D6D1CA]/45 text-[11px] tracking-[0.25em] uppercase mt-16 font-body">
            Trade portal · Architects &amp; designers can{" "}
            <button
              data-testid="collection-request-sample-btn"
              onClick={openSample}
              className="text-[#C9A876] link-underline tracking-[0.25em] uppercase"
            >
              request a sample
            </button>
            .
          </p>
        </section>
      </main>
      <Footer />
    </div>
  );
}
